import { useEffect, useMemo, useState } from 'react'
import { modelData } from 'app/lib/modelData'
import { closeModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useContactStore, useLocations, useTaskStore } from 'app/stores/StoreContext'
import type { ContactJson, TaskJson } from 'app/types/entities'
import { useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { assigneeInput, ROLE_ASSIGNEE_PREFIX } from 'lib/taskAssignee'
import { userHasLocationAccess } from 'lib/userLocationAccess'
import type { OptionType } from 'types/OptionType'
import { createContactOption, type ContactLike } from 'app/components/contacts/contactOption'
import { useResolveTaskOwner } from './useResolveTaskOwner'

/**
 * State and data for EditTaskModal, so the component only renders the form.
 *
 * - Locations come from LocationStore through `useLocations()`. The contact option is derived
 *   from that reactive list, so a later location load fills in the subtext without a re-fetch.
 * - The pre-filled contact is the edited task's contact, read through ContactStore, or the
 *   `defaultContact` prop until that read finishes.
 * - The owner is the task's role or user, resolved by `useResolveTaskOwner`.
 * - The contact picker searches through ContactStore, so its hits merge into the shared contacts.
 * - Saving goes through TaskStore (ADR-0001), and `taskStore.saving` drives the Save button.
 *
 * The caller must be an `observer`: the return values read store observables.
 */
export function useEditTaskModal({
  editingTask,
  defaultContact,
  onTaskCreatedOrUpdated,
  onTaskDelete,
}: {
  editingTask: TaskJson
  defaultContact?: ContactJson
  onTaskCreatedOrUpdated: (task?: TaskJson) => void
  onTaskDelete?: (taskId: string) => Promise<void>
}) {
  const workspaceId = useWorkspace()?.id
  const contactStore = useContactStore()
  const taskStore = useTaskStore()
  const resolveOwner = useResolveTaskOwner()
  const { list: locations, loading: locationsLoading } = useLocations()

  const [taskContact, setTaskContact] = useState<ContactLike | undefined>(undefined)
  const [pickedContact, setPickedContact] = useState<OptionType | undefined>(undefined)
  const [selectedOwner, setSelectedOwner] = useState<OptionType | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    setIsLoading(true)
    bg(async () => {
      const owner = await resolveOwner(editingTask)
      if (owner) setSelectedOwner({ ...owner, value: owner.id })
      if (editingTask.contactId) {
        const result = await contactStore.fetchOne(editingTask.contactId)
        const contact = (result.data as any)?.contact ?? result.data
        if (contact) setTaskContact(contact)
      }
      setIsLoading(false)
    })
    // Re-resolve only when a different task is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingTask])

  // The edited task's contact wins over the prop, matching the order the two loads finished in before.
  const loadedContact = taskContact ?? (workspaceId ? defaultContact : undefined)
  const loadedContactOption = useMemo(
    () => (loadedContact ? createContactOption(loadedContact, locations) : undefined),
    [loadedContact, locations],
  )
  const selectedContact = pickedContact ?? loadedContactOption

  const handleContactChange = (val: OptionType) => {
    setPickedContact(val)
    // Owners picked from the roster carry their scope; the preselected current user does not.
    const owner = selectedOwner?.meta?.locationAccess
    if (owner && val.meta?.locationId && !userHasLocationAccess(owner, val.meta.locationId, workspaceId)) {
      setSelectedOwner(undefined)
    }
    // Whether anyone holding a role can see the new location is only known once the picker re-reads it.
    if (selectedOwner?.id.startsWith(ROLE_ASSIGNEE_PREFIX) && val.meta?.locationId) {
      setSelectedOwner(undefined)
    }
  }

  const submitTask = async (formData: any) => {
    const contactId =
      (typeof formData?.contactId === `string`
        ? formData.contactId
        : (formData?.contactId?.id ?? selectedContact?.id)) || undefined
    const assignee: string | undefined =
      typeof formData?.ownerId === `string` ? formData.ownerId : (formData?.ownerId?.id ?? selectedOwner?.id)

    // No token resolution here: it would silently rewrite literal {{...}} text
    // in old tasks on unrelated saves.
    const input = {
      title: formData?.title ?? editingTask.title,
      description: formData?.description,
      dueDate: formData?.dueDate,
      contactId,
      ...assigneeInput(assignee),
      status: formData?.status ?? editingTask.status,
    }
    const { data, error } = await taskStore.update(editingTask.id!, input)
    if (error || !data?.updateTask) {
      notifyError(`Failed to save task${error?.message ? `: ${error.message}` : ``}`)
      return
    }
    notifySuccess(`Task saved`)
    closeModal()
    // TaskStore returns the mutation data with the task fields inline (no
    // fragment mask), so pass the plain object straight to modelData.
    onTaskCreatedOrUpdated(modelData(data.updateTask))
  }

  const deleteTask = async () => {
    setIsDeleting(true)
    if (onTaskDelete) await onTaskDelete(editingTask.id!)
  }

  return {
    workspaceId,
    locations,
    // Wait for the location list before the form shows, so the contact's subtext does not pop in.
    isLoading: isLoading || (locationsLoading && !locations.length),
    isSaving: taskStore.saving,
    isDeleting,
    selectedContact,
    selectedOwner,
    setSelectedOwner,
    // The picker shows the cached hits; the background server read refreshes the shared contacts
    // and urql's cache, so the next search shows the new list.
    searchContacts: (args: { query?: string; workspaceId?: string }) => contactStore.searchByName(args),
    handleContactChange,
    submitTask,
    deleteTask,
  }
}
